import React, { useEffect, useState } from "react";
import "../styling/HowToUse.css";
import tutorialVideo from "url:../assets/PrOCaRe Tutorial_v1.0.mp4";

const VIDEO_URL = tutorialVideo;

export default function HowToUse() {
	const [videoError, setVideoError] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	return (
		<main className="how-to-use-page">
			<article className="how-to-use-card">
				<header className="how-to-use-header">
					<h1>How to use PrOCaRe</h1>
				</header>

				<p className="how-to-use-video-intro">
					For a step-by-step demonstration, <strong>watch our <a href="#video-tutorial">video tutorial</a></strong>, which shows how to filter cases, explore aggregated clinical information, and examine the underlying publications. The main features and filtering options are also explained below.
				</p>

				<section className="how-to-use-section" aria-labelledby="views-heading">
					<h2 id="views-heading">Summary and Publications views</h2>
					<p>
						PrOCaRe organizes data at three levels: <strong>manuscript, patient, and treatment</strong>. A single publication may report multiple patients, and each patient may have received multiple treatments.
					</p>
					<p>
						The <strong>Summary</strong> view presents aggregated data in interactive panels that update dynamically as filters are applied. Only information on <strong>biomarker-guided treatments</strong> is included in this view.
					</p>
					<p>
						The <strong>Publications</strong> view provides detailed information about each individual publication, including patient characteristics, molecular findings, treatment sequences, clinical outcomes, adverse events, and links to the original publications. It also includes additional information not displayed in the Summary view, such as other reported lines of treatment that were not biomarker-guided.
					</p>
				</section>

				<section className="how-to-use-section" aria-labelledby="filtering-heading">
					<h2 id="filtering-heading">Filtering in the Summary view</h2>
					<p>
						Clicking a value in any panel applies a filter to the data, automatically updating all other panels to display only information from cases matching the selected criteria. Multiple filters can be combined to progressively narrow the results.
					</p>
					<ul>
						<li>
							<strong>Within the same panel, selections use OR logic:</strong> selecting multiple values retrieves cases matching <strong>any</strong> of the selected values. To select multiple values, simply click on each one. After the first selection, the remaining values appear greyed out but can still be selected by clicking on them.
						</li>
						<li>
							<strong>Across different panels, selections use AND logic:</strong> selecting values in different panels retrieves only cases matching <strong>all</strong> the selected criteria.
						</li>
					</ul>
					<p>
						For example, selecting <em>EGFR</em> and <em>MET</em> in the Biomarker panel retrieves cases involving either biomarker, whereas selecting <em>EGFR</em> in the Biomarker panel and <em>Lung</em> in the Primary Site panel retrieves only cases matching both criteria.
					</p>
					<p>
						All active filters are displayed at the <strong>top of the page</strong>, where they can be reviewed, modified, or removed.
					</p>
					<p>
						For further information about individual panels, click the <strong>tooltip (?) icon</strong> next to each panel's title.
					</p>
				</section>

				<section className="how-to-use-section" aria-labelledby="searching-heading">
					<h2 id="searching-heading">Searching within panels of the Summary view</h2>
					<p>
						The search bar in each panel uses <strong>partial text matching</strong>, displaying entries containing the search term rather than requiring an exact match.
					</p>
					<p>
						Multiple search terms are combined using <strong>AND logic</strong>. For example, searching for <code>EGFR MET</code> in the Biomarker Details panel displays entries containing both EGFR and MET, such as <code>EGFR:L858R AND MET:amplification</code>, while excluding entries containing only one of these biomarkers.
					</p>
					<p>
						The <strong>Select All</strong> option selects all entries matching the current search. Since multiple selections within a panel use OR logic, these entries are combined using OR when applied as a filter.
					</p>
				</section>

				<section className="how-to-use-section" aria-labelledby="navigation-heading">
					<h2 id="navigation-heading">Navigating between views</h2>
					<p>
						<strong>Filters are shared between the Summary and Publications views.</strong> Filters applied in the Summary view determine which publications appear in the Publications view.
					</p>
					<p>
						Conversely, individual publications can be selected in the Publications view to filter the Summary view, allowing users to explore aggregated information from one or more selected publications.
					</p>
				</section>

				<section id="video-tutorial" className="how-to-use-video-section" aria-labelledby="video-heading">
					<h2 id="video-heading">Video tutorial</h2>
					{VIDEO_URL ? (
						<>
							<video className="how-to-use-video" controls playsInline preload="none" aria-label="PrOCaRe video tutorial" onError={() => setVideoError(true)}>
								<source src={VIDEO_URL} onError={() => setVideoError(true)} />
								Your browser does not support embedded video. <a href={VIDEO_URL}>Open the video tutorial</a>.
							</video>
							{videoError && <p className="how-to-use-video-message" role="status">The video could not be loaded. Please try again later.</p>}
						</>
					) : (
						<div className="how-to-use-video-placeholder">
							<svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
								<circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="1.5" />
								<path d="M17 13L27 20L17 27V13Z" fill="currentColor" />
							</svg>
							<p>Video tutorial coming soon.</p>
						</div>
					)}
				</section>
			</article>
		</main>
	);
}
